import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';
import { useQueryClient } from '@tanstack/react-query';

import type { TResolvedFile } from '@/modules/outputs/types';
import { captureException } from '@/modules/sentry';

import type { IRainThreadAdapter } from './scope';

export interface IUseRainArtifactPreviewReturn {
  previewFile: TResolvedFile | null;
  previewLoadingName: string | null;
  handleAttachmentClick: (attachment: {
    uri: string;
    filename: string;
  }) => Promise<void>;
  closePreview: () => void;
}

/**
 * Resolves a clicked chat attachment into a previewable file, scope-agnostic
 * via the thread adapter. Image attachments carry their bytes inline as a data
 * URI (open instantly); documents are artifacts in the `uploads://` store, so
 * we show a spinner while fetching their (base64) content through the adapter
 * (cached, so re-opens are instant). `previewRequestRef` tags each open so a
 * closed or superseded click can't pop a stale preview later.
 */
export function useRainArtifactPreview(
  adapter: IRainThreadAdapter,
): IUseRainArtifactPreviewReturn {
  const { t } = useTranslation('rain');
  const queryClient = useQueryClient();

  const [previewFile, setPreviewFile] = useState<TResolvedFile | null>(null);
  const [previewLoadingName, setPreviewLoadingName] = useState<string | null>(
    null,
  );
  const previewRequestRef = useRef(0);

  const { fetchArtifactContent, getArtifactContentQueryKey } = adapter;

  const closePreview = useCallback(() => {
    previewRequestRef.current += 1;
    setPreviewLoadingName(null);
    setPreviewFile(null);
  }, []);

  const handleAttachmentClick = useCallback(
    async ({ uri, filename }: { uri: string; filename: string }) => {
      const requestId = (previewRequestRef.current += 1);

      if (uri.startsWith('data:')) {
        const base64 = uri.slice(uri.indexOf(',') + 1);
        setPreviewLoadingName(null);
        setPreviewFile({
          type: 'file-inline',
          name: filename,
          content: base64,
        });
        return;
      }

      // Open the layover right away with a spinner, then fill in content.
      setPreviewFile(null);
      setPreviewLoadingName(filename);
      try {
        const response = await queryClient.fetchQuery({
          queryKey: getArtifactContentQueryKey(uri),
          queryFn: () => fetchArtifactContent(uri),
          staleTime: 5 * 60 * 1000,
        });
        // Ignore if the user closed the preview or clicked another attachment.
        if (previewRequestRef.current !== requestId) return;
        const content = response.data.data;
        if (!content) throw new Error('Empty artifact content');
        setPreviewLoadingName(null);
        setPreviewFile({ type: 'file-inline', name: filename, content });
      } catch (error) {
        if (previewRequestRef.current !== requestId) return;
        setPreviewLoadingName(null);
        toast.error(t('rain.error.previewAttachment'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'preview_attachment' },
        });
      }
    },
    [fetchArtifactContent, getArtifactContentQueryKey, queryClient, t],
  );

  return {
    previewFile,
    previewLoadingName,
    handleAttachmentClick,
    closePreview,
  };
}
